import React, { FC, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ErrorModal from "components/Modal/errorModal";
import SuccessModal from "components/Modal/successModal";
import UnaffiliatedModal from "components/Modal/unaffiliatedModal";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import config from "config.json";
import { Helmet } from "react-helmet";

const ValidatePhone: FC = () => {
  const location = useLocation();  // Para obtener los query params
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isUnaffiliatedModalOpen, setIsUnaffiliatedModalOpen] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false); // Para manejar si se muestra el input de teléfono
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const url = config.backend_url;
  // const url = "http://localhost:3001"

  // Obtener el user desde los query params
  const queryParams = new URLSearchParams(location.search);
  const user = queryParams.get("user");

  // Validar si el id del usuario está presente y es un número válido
  useEffect(() => {
    if (!user || isNaN(Number(user))) {
      setErrorMessage("Hubo un error al cargar los datos.");
      setIsErrorModalOpen(true);
    } else {
      requestPhoneActivation(Number(user)); // Asegurarse de que se pase el id como número
    }
  }, [user]);

  const requestPhoneActivation = async (id: number) => {
    try {
      const response = await fetch(`${url}/authe/request_phone_activation?id=${id}`);
      const data = await response.json();
      if (data.code === "200") {
        // Success, now show the code input
        setVerificationCode("");
      } else if (data.code === "901") {
        // User not affiliated
        setIsUnaffiliatedModalOpen(true);
      } else {
        setErrorMessage("Error al solicitar la activación del teléfono.");
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setErrorMessage("Error al conectar con el servidor.");
      setIsErrorModalOpen(true);
    }
  };

  const handleValidateCode = async () => {
    if (verificationCode.length !== 6) {
      setErrorMessage("El código debe tener 6 dígitos.");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      const response = await fetch(`${url}/authe/confirm_phone?id=${user}&token=${verificationCode}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.code === "200") {
        setIsSuccessModalOpen(true);
      } else if (data.code === "901") {
        setIsUnaffiliatedModalOpen(true);
      } else {
        setErrorMessage("Error al confirmar el código.");
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setErrorMessage("Error al conectar con el servidor.");
      setIsErrorModalOpen(true);
    }
  };

  // Función para validar el número de teléfono argentino
  const telArgentinoValido = (tel: string) => {
    const num = tel.replace(/\D+/g, ""); // Eliminamos todo lo que no es dígito
    const regex = /^(11|[2368]\d{2})\d{6,8}$/; // Expresión regular corregida para teléfonos argentinos
    return regex.test(num);
  };

  const handlePhoneChangeRequest = async () => {
    if (phoneNumber.trim() === "") {
      setErrorMessage("El número de teléfono no puede estar vacío.");
      setIsErrorModalOpen(true);
      return;
    }

    if (!telArgentinoValido(phoneNumber)) {
      setErrorMessage("El número de teléfono no tiene el formato correcto. Ejemplo: 1163091332");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      const response = await fetch(`${url}/authe/request_phone_change_activation?id=${user}&phone=${phoneNumber}`);
      const data = await response.json();
      if (data.code === "200") {
        // Success, show input for verification code
        setShowPhoneInput(false);
        setVerificationCode("");
      } else if (data.code === "901") {
        setIsUnaffiliatedModalOpen(true);
      } else {
        setErrorMessage("Error al solicitar el cambio de teléfono.");
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setErrorMessage("Error al conectar con el servidor.");
      setIsErrorModalOpen(true);
    }
  };

  if (!user || isNaN(Number(user))) {
    return (
      <div className="container flex flex-col w-full text-center h-screen items-center justify-center mx-auto p-4">
        <p className="text-red-500">Error al cargar los datos</p>
        <button className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-full" onClick={() => window.location.href = '/login'}>Volver al Inicio de Sesión</button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 h-screen mt-20">
      <Helmet>
        <title>Validar Teléfono - Setia</title>
      </Helmet>
      <h2 className="text-3xl font-semibold text-center">Validación de Teléfono</h2>

      {!showPhoneInput ? (
        <>
          <div className="mt-6 flex flex-col items-center">
            <p className="text-center">Ingresa el código que recibiste por WhatsApp</p>
            <input
              type="number"
              placeholder="Ingresa el código de 6 dígitos"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="mt-4 max-w-xs"
            />
            <ButtonPrimary onClick={handleValidateCode} className="mt-4">
              Validar Código
            </ButtonPrimary>
          </div>

          <p className="mt-6 text-center">
            No recibiste el código?{" "}
            <span
              className="text-primary-700 cursor-pointer"
              onClick={() => setShowPhoneInput(true)}
            >
              Ingresa Acá
            </span>
          </p>
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center">
          <p className="text-center">Ingresa tu número de teléfono para enviar el código</p>
          <input
            type="number"
            placeholder="Ej: 1112345678"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="mt-4 max-w-xs"
          />
          <p className="text-center font-semibold text-sm mt-4 p-2 bg-gray-200 rounded-xl border ">
            Si este número es diferente al que registraste, se actualizará en tu cuenta al confirmar el código.
          </p>
          <ButtonPrimary onClick={handlePhoneChangeRequest} className="mt-4">
            Enviar Código
          </ButtonPrimary>
        </div>
      )}

      {/* Modales */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        handleModalClose={() => navigate("/login")}
        title="Bienvenido"
        message="Ya podes iniciar sesión en tu cuenta"
      />
      <ErrorModal
        isModalOpen={isErrorModalOpen}
        handleModalClose={() => setIsErrorModalOpen(false)}
        message={errorMessage}
      />
      <UnaffiliatedModal
        isModalOpen={isUnaffiliatedModalOpen}
        handleModalClose={() => {
          setIsUnaffiliatedModalOpen(false);
          navigate("/");
        }}
      />
    </div>
  );
};

export default ValidatePhone;
